import {
  accountBalanceAtom,
  appEventDispatcher,
  AppEvents,
  AuthState,
  ConnectionState,
  currencyExchangeRateRTAtom,
  eStreamSessionIdAtom,
  EStreamTradeDTO,
  IConfig,
  InstrumentStore,
  inventoryDetailsAtom,
  ISSEManager,
  Keys,
  NbLogger,
  onAppEvent,
  SSECommunicatorBase,
  SSESource,
  Store,
  UserStreamMessage,
} from '@eih-netbroker/common';
import { Config } from '../../config/config';
import { instrumentStore } from '../../data/instrumentStore';
import { primaryInstanceAtom } from '../../state/primaryInstanceAtom';
import { store } from '../../state/store';
import { SseEventLogger } from './sseEventLogger';
import { sseManager } from './sseManager';
import ThisInstanceSelectedAsPrimaryEvent = AppEvents.ThisInstanceSelectedAsPrimaryEvent;
import EStreamSubscriptionChangeEvent = AppEvents.EStreamSubscriptionChangeEvent;
import SSEConnectionStateUpdateEvent = AppEvents.SSEConnectionStateUpdateEvent;
import EStreamTradesReceivedEvent = AppEvents.EStreamTradesReceivedEvent;
import OtherInstanceLoggedOutEvent = AppEvents.OtherInstanceLoggedOutEvent;

type SSECommunicatorProps = {
  offlineMode: boolean;
  createLogger: (name: string) => NbLogger;
};

const logger = SseEventLogger.getLogger();

export class SSECommunicatorWeb extends SSECommunicatorBase {
  private static instance: SSECommunicatorWeb;

  static create(props: SSECommunicatorProps) {
    if (this.instance) {
      return this.instance;
    }
    this.instance = new SSECommunicatorWeb(
      props.offlineMode,
      props.createLogger,
      Config,
      store,
      sseManager,
      instrumentStore,
    );
    return this.instance;
  }

  /** Marks if this instance is the primary instance, meaning this handles SSE connection */
  protected override set thisInstanceIsPrimary(value: boolean) {
    store.set(primaryInstanceAtom, value);
  }

  protected override get thisInstanceIsPrimary() {
    return store.get(primaryInstanceAtom);
  }

  private get inventoryDetails() {
    return store.get(inventoryDetailsAtom);
  }

  private get accountBalance() {
    return store.get(accountBalanceAtom).values();
  }

  private get currencyExchangeRates() {
    return store.get(currencyExchangeRateRTAtom);
  }

  private constructor(
    protected offlineMode: boolean,
    protected createLogger: (name: string) => NbLogger,
    protected config: IConfig,
    protected store: Store,
    protected sseManager: ISSEManager,
    protected instrumentStore: InstrumentStore,
  ) {
    super(
      offlineMode,
      createLogger,
      config,
      store,
      sseManager,
      instrumentStore,
      (token: string) => {
        const params = [
          token ? `token=${encodeURIComponent(token)}` : '',
          `trade_mics=${Keys.Markets.dataProviderMicList.bet.join(',')}`,
        ];
        return new EventSource(`${config.SSE.eStreamUrl}?${params.join('&')}`, {
          withCredentials: true,
        });
      },
      () => new EventSource(config.SSE.userStreamUrl, { withCredentials: true }),
    );

    // init event listeners
    onAppEvent(ThisInstanceSelectedAsPrimaryEvent.type, (evt: ThisInstanceSelectedAsPrimaryEvent) => {
      this.onThisInstanceSelectedAsPrimary(evt.data);
    });
    onAppEvent(SSEConnectionStateUpdateEvent.type, (evt: SSEConnectionStateUpdateEvent) => {
      this.onSSEConnectionStateChangeInternal(evt.data.source, evt.data.connectionState, true);
    });
    onAppEvent(EStreamTradesReceivedEvent.type, (evt: EStreamTradesReceivedEvent) => {
      this.onEStreamTradesReceived(evt.data.trades);
    });
    onAppEvent(EStreamSubscriptionChangeEvent.type, (evt: EStreamSubscriptionChangeEvent) => {
      this.onEStreamSubscriptionChange(evt.data.dir, evt.data.options, evt.data.instanceId);
    });
    onAppEvent(OtherInstanceLoggedOutEvent.type, () => {
      this.onOtherInstanceLoggedOutInternal();
    });
  }

  protected override dispatchSSEStateUpdateEvent(
    source: SSESource,
  ): (connectionState: ConnectionState, broadcastConnectionState: boolean) => void {
    return (connectionState: ConnectionState, broadcastConnectionState: boolean) => {
      appEventDispatcher.dispatchEvent(
        new SSEConnectionStateUpdateEvent({
          source,
          connectionState,
          isInitalValue: broadcastConnectionState,
        }),
      );

      const eStreamSessionId = store.get(eStreamSessionIdAtom);
      if (
        this.authState.authStatus === AuthState.Authenticated &&
        this.thisInstanceIsPrimary &&
        sseManager.isPrimaryInstanceSelected &&
        eStreamSessionId
      ) {
        if (this.authState.authStatus !== AuthState.Authenticated || !this.authState.secrets) {
          this.logger.error(
            `Auth state is not authenticated, but eStreamSessionId is set: ${eStreamSessionId} - ${JSON.stringify(
              this.authState,
            )}`,
          );
          throw new Error(
            `Auth state is not authenticated, but eStreamSessionId is set: ${eStreamSessionId} - ${JSON.stringify(
              this.authState,
            )}`,
          );
        }
        sseManager.broadcastLogin({
          jwtToken: this.authState.secrets.JwtToken,
          idToken: this.authState.secrets.idToken,
          eStreamSessionId,
          userStreamConnectionState: this.userStreamConnection.getConnectionState(),
          eStreamConnectionState: this.eStreamConnection.getConnectionState(),
          inventoryDetails: this.inventoryDetails,
          accountBalance: this.accountBalance,
          currencyExchangeRates: this.currencyExchangeRates,
        });
      }
    };
  }

  initSSEManager(): Promise<boolean | null> {
    return sseManager.initAndCheckIfWeNeedToLogin().then(async (shouldLoadLogin: boolean | null) => {
      if (shouldLoadLogin === false) {
        // ask for instrument store
        const data = await sseManager.getInstrumentStore();
        if (data) {
          this.logger.debug(`Received instrument store data (${data.length} items: ${data.map(_ => _.ric).join(',')})`);
          instrumentStore.setInstrumentStoreItem(data);
        } else {
          this.logger.debug(`No instrument store data received`);
        }
      }
      return shouldLoadLogin;
    });
  }

  override destroy() {
    const remainingSubscriptions = this.eStreamConnection.removeEStreamSubscriptionsByInstanceId(sseManager.instanceId);
    sseManager.destroy(remainingSubscriptions);
  }

  override async disconnect(force?: boolean): Promise<void> {
    if (this.thisInstanceIsPrimary) {
      // first you need to clear the token to mark that we are not supposed to be connected
      this.userStreamConnection.setToken('');
      this.eStreamConnection.setToken('');

      // then disconnect the clients
      this.userStreamConnection.disconnectSSEClient(force);
      this.eStreamConnection.disconnectSSEClient(force);
    }
  }

  takeOverPrimaryInstance() {
    return sseManager.takeOverPrimaryInstance();
  }

  protected override onUserStreamMessageReceivedInternal(message: UserStreamMessage) {
    this.logger.trace(`User stream message received: ${JSON.stringify(message)}`);

    // if we are the primary instance we need other instances to know about the UserStream messages
    if (this.thisInstanceIsPrimary) {
      sseManager.broadcastUserStreamMessage(message);

      appEventDispatcher.dispatchEvent(new AppEvents.UserStreamMessageEvent(message, false));
    }
  }

  protected override onEStreamTradesReceived(trades: Array<EStreamTradeDTO> | EStreamTradeDTO) {
    super.onEStreamTradesReceived(trades);
    if (logger) {
      logger(trades);
    }
  }

  /**
   * User is signed off in another tab, se we can just refresh the page, theoretically user won't be logged in
   */
  private async onOtherInstanceLoggedOutInternal() {
    if (this.authState?.isAuthenticated) {
      this.sseConnections.forEach(c => c.disconnectSSEClient());
    }
  }

  private onOtherInstanceDestroyed(instanceId: number) {
    if (this.thisInstanceIsPrimary) {
      this.eStreamConnection.removeEStreamSubscriptionsByInstanceId(instanceId);
    }
  }
}
